<script setup lang="ts">
import config from "@/config";
import { AssociationService } from "@/service/AssociationService";
import { useAppStore } from "@/store/app";
import {
  formatCardinality,
  generateCacheKey,
  getCustomObjectLabels,
  getDefaultAssociationSelection,
  hasCustomObjectFields,
  isOnlyCustomObjectField,
} from "@/util/associationUtils";
import { ASSOCIATION_CONSTANTS, PRODUCT_TYPE } from "@/util/constants";
import { hasContactFields } from "@/util/customObjectUtils";
import {
  ChevronDownIcon,
  CursorBoxIcon,
  PlusIcon,
  SearchLgIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UIModal,
  UIModalContent,
  UIModalFooter,
  UIModalHeader,
} from "@gohighlevel/ghl-ui";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const store = useAppStore();

/**
 * Props interface for the contact association modal
 */
interface Props {
  show: boolean;
  objectModel?: string; // The custom object model (e.g., "custom_objects.doctors")
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "update:cancel": [value: boolean];
  "update:success": [settings: ContactAssociationSettings];
}>();

/**
 * Interface for contact association settings
 */
interface ContactAssociationSettings {
  selectedAssociation: string[];
  objectModel?: string;
  createNewAssociation?: boolean;
}

/**
 * Interface for association option in dropdown
 */
interface AssociationOption {
  label: string;
  value: string;
  description: string;
}

/**
 * Interface for API association response
 */
interface AssociationResponse {
  data?: {
    associations?: Array<{
      id: string;
      key?: string;
      firstObjectLabel?: string;
      firstObjectToSecondObjectCardinality?: string;
    }>;
  };
}

/**
 * Association options fetched from API
 */
const associationOptions = ref<AssociationOption[]>([]);
const loading = ref<boolean>(false);
const error = ref<string>("");
const isDropdownOpen = ref<boolean>(false);
const dropdownRef = ref<HTMLElement | null>(null);
const usingCachedData = ref<boolean>(false);
const isInitializing = ref<boolean>(false); // Flag to prevent duplicate API calls

/**
 * Close dropdown when clicking outside
 */
const closeDropdownOnClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isDropdownOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", closeDropdownOnClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", closeDropdownOnClickOutside);
});

/**
 * Form data for the modal
 */
const associationSettings = ref<ContactAssociationSettings>({
  selectedAssociation: [],
  objectModel: props.objectModel,
});

/**
 * Check if there are contact fields in the form/survey/quiz
 */
const hasContactFieldsPresent = computed(() => {
  return hasContactFields(
    store.product === PRODUCT_TYPE.FORM ? store.elements : null,
    store.product !== PRODUCT_TYPE.FORM ? store.slides : null
  );
});

/**
 * Get selected option display info
 */
const selectedOption = computed(() => {
  return associationOptions.value.find(
    (option) => option.value === associationSettings.value.selectedAssociation[0]
  );
});

/**
 * Set default association selection based on saved settings or first available option
 */
const setDefaultAssociationSelection = (): void => {
  // Check if there are contact fields in the form/survey/quiz
  const hasContactFieldsPresent = hasContactFields(
    store.product === PRODUCT_TYPE.FORM ? store.elements : null,
    store.product !== PRODUCT_TYPE.FORM ? store.slides : null
  );

  // If no contact fields, default to "None" (empty string)
  if (!hasContactFieldsPresent) {
    associationSettings.value.selectedAssociation = [];
    return;
  }

  // Otherwise, use the normal default selection logic
  const selectedAssociation = getDefaultAssociationSelection(
    associationOptions.value,
    store.contactAssociationSettings,
    props.objectModel
  );
  associationSettings.value.selectedAssociation = selectedAssociation ? [selectedAssociation] : [];
};

/**
 * Get the correct object labels from store
 */
const getObjectLabels = computed(() => {
  return getCustomObjectLabels(store.customObjects || [], props.objectModel);
});

/**
 * Fetch associations from API or cache
 */
const fetchAssociations = async () => {
  if (!props.objectModel || !store.locationId) {
    return;
  }

  // Prevent duplicate calls
  if (loading.value || isInitializing.value) {
    return;
  }

  // Check cache first
  const cacheKey = generateCacheKey(props.objectModel, store.locationId);
  const cachedData = store.associationCache.get(cacheKey);

  // Check if cache has expired
  const isCacheExpired =
    cachedData && Date.now() - cachedData.timestamp > ASSOCIATION_CONSTANTS.CACHE_EXPIRY_MS;

  if (cachedData && !isCacheExpired) {
    // Use cached data
    associationOptions.value = cachedData.associations;
    usingCachedData.value = true;

    // Set selection based on saved settings or first option
    setDefaultAssociationSelection();
    return;
  }

  try {
    loading.value = true;
    isInitializing.value = true;
    error.value = "";
    usingCachedData.value = false;

    const response: AssociationResponse = await AssociationService.getAssociations(
      props.objectModel,
      store.locationId,
      ASSOCIATION_CONSTANTS.CONTACT_OBJECT_TYPE
    );

    // Transform API response to dropdown options
    const associations = response.data?.associations || [];
    const transformedAssociations: AssociationOption[] = associations.map((association) => {
      // Clean up cardinality - convert MANY_TO_MANY to "Many to Many"
      const cardinality = formatCardinality(
        association.firstObjectToSecondObjectCardinality,
        t("common.manyToMany")
      );
      const objectLabels = getObjectLabels.value;
      const description = `Associations: ${cardinality}, ${objectLabels.plural}`;

      return {
        label: association.firstObjectLabel || association.key || t("common.unknownAssociation"),
        value: association.id,
        description: description,
      };
    });

    associationOptions.value = transformedAssociations;
    usingCachedData.value = false;

    // Cache the result
    store.associationCache.set(cacheKey, {
      associations: transformedAssociations,
      timestamp: Date.now(),
    });

    // Set default selection if options are available
    setDefaultAssociationSelection();
  } catch (err) {
    // Log error for debugging but use proper error handling for user
    if (process.env.NODE_ENV === "development") {
      console.error("Failed to fetch associations:", err);
    }
    error.value = t("common.failedToLoadAssociations");
    usingCachedData.value = false;
    // Don't provide fallback options - leave empty to show disabled state
    associationOptions.value = [];
    associationSettings.value.selectedAssociation = [];
  } finally {
    loading.value = false;
    isInitializing.value = false;
  }
};

/**
 * Select an option from the dropdown
 */
const selectOption = (option: any) => {
  associationSettings.value.selectedAssociation = option.value ? [option.value] : [];
  isDropdownOpen.value = false;
};

/**
 * Computed property to ensure disabled state is always boolean
 */
const isDropdownDisabled = computed(() => {
  return loading.value || !!error.value;
});

/**
 * Watch for modal open and fetch associations
 */
watch(
  () => props.show,
  (newShow) => {
    if (newShow && props.objectModel) {
      // Initialize with saved settings if they exist for this object model
      const savedAssociation = store.contactAssociationSettings;
      if (savedAssociation?.objectModel === props.objectModel) {
        const savedSelection = savedAssociation.selectedAssociation;
        associationSettings.value = {
          selectedAssociation: Array.isArray(savedSelection)
            ? savedSelection
            : savedSelection
            ? [savedSelection]
            : [],
          objectModel: props.objectModel,
        };
      } else {
        // Reset to defaults if no saved settings for this object model
        associationSettings.value = {
          selectedAssociation: [],
          objectModel: props.objectModel,
        };
      }

      // Only fetch if we don't already have data for this object model
      const cacheKey = generateCacheKey(props.objectModel, store.locationId);
      const cachedData = store.associationCache.get(cacheKey);
      const isCacheExpired =
        cachedData && Date.now() - cachedData.timestamp > ASSOCIATION_CONSTANTS.CACHE_EXPIRY_MS;

      if (!cachedData || isCacheExpired) {
        fetchAssociations();
      } else {
        // Use cached data without making API call
        associationOptions.value = cachedData.associations;
        usingCachedData.value = true;
        setDefaultAssociationSelection();
      }
    }
  },
  { immediate: true }
);

/**
 * Watch for objectModel changes
 */
watch(
  () => props.objectModel,
  (newObjectModel, oldObjectModel) => {
    associationSettings.value.objectModel = newObjectModel;

    // Only fetch if modal is shown and object model actually changed
    if (props.show && newObjectModel && newObjectModel !== oldObjectModel) {
      // Check if we already have cached data before making API call
      const cacheKey = generateCacheKey(newObjectModel, store.locationId);
      const cachedData = store.associationCache.get(cacheKey);
      const isCacheExpired =
        cachedData && Date.now() - cachedData.timestamp > ASSOCIATION_CONSTANTS.CACHE_EXPIRY_MS;

      if (!cachedData || isCacheExpired) {
        fetchAssociations();
      } else {
        // Use cached data without making API call
        associationOptions.value = cachedData.associations;
        usingCachedData.value = true;
        setDefaultAssociationSelection();
      }
    }
  }
);

/**
 * Check if this is the first custom object field of this type
 */
const isFirstCustomObjectField = computed(() => {
  return isOnlyCustomObjectField(
    store.product === PRODUCT_TYPE.FORM ? store.elements : null,
    store.product !== PRODUCT_TYPE.FORM ? store.slides : null,
    props.objectModel
  );
});

/**
 * Get the current custom object schema
 */
const getCurrentCustomObject = computed(() => {
  return store.customObjects?.find((obj) => obj.key === props.objectModel);
});

/**
 * Check if primary display property field already exists
 */
const primaryDisplayPropertyExists = computed(() => {
  const currentObject = getCurrentCustomObject.value;
  if (!currentObject?.primaryDisplayProperty) {
    return true;
  }

  const allFields =
    store.product === PRODUCT_TYPE.FORM
      ? store.elements
      : store.slides?.flatMap((slide) => slide.slideData || []);

  return allFields?.some(
    (element) =>
      element.fieldKey === currentObject.primaryDisplayProperty ||
      element.tag === currentObject.primaryDisplayProperty
  );
});

/**
 * Find the current slide index where the custom object field was dropped
 */
const getCurrentSlideIndex = computed(() => {
  if (store.product === PRODUCT_TYPE.FORM) {
    return null;
  }

  for (let i = 0; i < store.slides.length; i++) {
    const slide = store.slides[i];
    if (slide.slideData?.some((element) => element.model === props.objectModel)) {
      return i;
    }
  }
  return ASSOCIATION_CONSTANTS.DEFAULT_SLIDE_INDEX; // Default to first slide if not found
});

/**
 * Check if any custom object fields of this type remain
 */
const hasRemainingCustomObjectFields = computed(() => {
  return hasCustomObjectFields(
    store.product === PRODUCT_TYPE.FORM ? store.elements : null,
    store.product !== PRODUCT_TYPE.FORM ? store.slides : null,
    props.objectModel
  );
});

/**
 * Handle modal cancel action
 */
const cancelCallback = () => {
  // If this is the first custom object field, remove it
  if (isFirstCustomObjectField.value) {
    if (store.product === PRODUCT_TYPE.FORM) {
      // Remove from elements array
      const elementIndex = store.elements.findIndex(
        (element) => element.model === props.objectModel
      );
      if (elementIndex !== -1) {
        store.elements.splice(elementIndex, 1);
      }
    } else {
      // Remove from slides
      store.slides.forEach((slide) => {
        if (slide.slideData) {
          const elementIndex = slide.slideData.findIndex(
            (element) => element.model === props.objectModel
          );
          if (elementIndex !== -1) {
            slide.slideData.splice(elementIndex, 1);
          }
        }
      });
    }

    // Check if any custom object fields remain, if not, clear association
    if (!hasRemainingCustomObjectFields.value) {
      // Clear association settings for this specific object model
      if (store.contactAssociationSettings?.objectModel === props.objectModel) {
        store.contactAssociationSettings = null;
      }
      store.currentCustomObjectModel = null;
    }
  }

  // Reset modal state
  associationSettings.value = {
    selectedAssociation: associationOptions.value[0]?.value
      ? [associationOptions.value[0].value]
      : [],
    objectModel: props.objectModel,
  };
  error.value = "";
  isDropdownOpen.value = false;
  emit("update:cancel", false);
};

/**
 * Handle modal save action
 */
const submitCallback = () => {
  // Auto-add primaryDisplayProperty if needed
  const currentObject = getCurrentCustomObject.value;
  if (currentObject?.primaryDisplayProperty && !primaryDisplayPropertyExists.value) {
    // Find the existing custom field from store
    const primaryField = store.customFields?.find(
      (field) =>
        field.fieldKey === currentObject.primaryDisplayProperty ||
        field.fieldKey ===
          `${currentObject.key}.${currentObject.primaryDisplayProperty.split(".").pop()}`
    );

    if (primaryField) {
      // Use the existing custom field with proper structure
      const fieldToAdd = {
        ...primaryField,
        active: false,
        edit: false,
        required: true,
      };

      if (store.product === PRODUCT_TYPE.FORM) {
        // Find the dropped custom object element and add primary field after it
        const droppedElementIndex = store.elements.findIndex(
          (element) => element.model === props.objectModel
        );
        if (droppedElementIndex !== -1) {
          // Insert primary field right after the dropped element
          store.elements.splice(droppedElementIndex + 1, 0, fieldToAdd);
        } else {
          // Fallback: add to end if dropped element not found
          store.elements.push(fieldToAdd);
        }
      } else {
        // Add to the current slide where the element was dropped
        const currentSlideIndex = getCurrentSlideIndex.value;
        if (currentSlideIndex !== null && store.slides[currentSlideIndex]) {
          store.slides[currentSlideIndex].slideData =
            store.slides[currentSlideIndex].slideData || [];
          // Find the dropped custom object element and add primary field after it
          const droppedElementIndex = store.slides[currentSlideIndex].slideData.findIndex(
            (element) => element.model === props.objectModel
          );
          if (droppedElementIndex !== -1) {
            // Insert primary field right after the dropped element
            store.slides[currentSlideIndex].slideData.splice(
              droppedElementIndex + 1,
              0,
              fieldToAdd
            );
          } else {
            // Fallback: add to end if dropped element not found
            store.slides[currentSlideIndex].slideData.push(fieldToAdd);
          }
        } else {
          // Fallback: create new slide if necessary
          if (store.slides.length === 0) {
            store.slides.push({
              slideData: [fieldToAdd],
              slideId: `slide_${Date.now()}`,
            });
          } else {
            store.slides[0].slideData = store.slides[0].slideData || [];
            store.slides[0].slideData.push(fieldToAdd);
          }
        }
      }
    } else {
      // Log warning for debugging in development mode
      if (process.env.NODE_ENV === "development") {
        console.warn(
          `Primary display property field not found in customFields: ${currentObject.primaryDisplayProperty}`
        );
      }
    }
  }

  // Store association settings with object model reference
  const settingsWithModel = {
    ...associationSettings.value,
    objectModel: props.objectModel,
  };

  emit("update:success", settingsWithModel);
};

/**
 * Handle create new association action
 */
const createNewAssociation = () => {
  const currentObject = getCurrentCustomObject.value;
  if (!currentObject?.id) {
    // Log error for debugging in development mode
    if (process.env.NODE_ENV === "development") {
      console.error("No custom object ID found for creating association");
    }
    return;
  }

  // Clear cache for this object model so we fetch fresh data next time
  store.clearAssociationCache(props.objectModel);

  // Open the associations page for this custom object
  const associationsUrl = `${config.mode === "dev" ? "http" : "https"}://${
    store.company.domain ? store.company.domain : config.GHL_DOMAIN
  }/v2/location/${store.locationId}/settings/objects/${currentObject.id}/associations`;

  window.open(associationsUrl, "_blank");
};
</script>

<template>
  <UIModal :show="show" @update:show="cancelCallback">
    <template #header>
      <UIModalHeader
        id="contact-association-modal-header"
        :title="t('common.selectContactAssociation')"
        :icon="CursorBoxIcon"
        @close="cancelCallback"
      >
      </UIModalHeader>
    </template>

    <UIModalContent>
      <div class="mb-6">
        <div id="associations-label" class="mb-1 text-sm font-medium">
          {{ t("common.associations") }}
        </div>

        <!-- Custom Dropdown -->
        <div ref="dropdownRef" class="relative">
          <!-- Dropdown Button -->
          <button
            type="button"
            class="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-3 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
            :disabled="isDropdownDisabled"
            :aria-expanded="isDropdownOpen"
            :aria-haspopup="true"
            role="combobox"
            :aria-label="t('common.associations')"
            @click="isDropdownOpen = !isDropdownOpen"
          >
            <div v-if="selectedOption" class="block">
              <div class="text-sm font-medium text-gray-900">{{ selectedOption.label }}</div>
              <div class="mt-1 text-sm text-gray-500">{{ selectedOption.description }}</div>
            </div>
            <div
              v-else-if="
                !hasContactFieldsPresent && associationSettings.selectedAssociation.length === 0
              "
              class="block"
            >
              <div class="text-sm font-medium text-gray-900">{{ t("common.noneAssociation") }}</div>
              <div class="mt-1 text-sm text-gray-500">
                {{ t("common.noneAssociationDescription") }}
              </div>
            </div>
            <div v-else class="block text-gray-500">{{ t("common.selectAnAssociation") }}</div>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon class="h-5 w-5 text-gray-400" />
            </span>
          </button>

          <!-- Dropdown Menu -->
          <div
            v-if="isDropdownOpen && !isDropdownDisabled"
            class="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            role="listbox"
            :aria-labelledby="'associations-label'"
          >
            <!-- Empty State -->
            <div v-if="associationOptions.length === 0" class="px-2 py-4 text-center">
              <!-- Empty State Icon -->
              <div
                class="mx-auto mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary-100"
              >
                <SearchLgIcon class="h-4 w-4 text-primary-600" />
              </div>

              <!-- Empty State Text -->
              <div class="mb-2 text-sm font-medium text-gray-900">
                {{ t("common.noAssociationsFound") }}
              </div>

              <!-- Empty State Button -->
              <button
                id="empty-state-create-association-btn"
                type="button"
                class="inline-flex items-center rounded border border-0 bg-[#eff4ff] px-3.5 py-2 text-sm font-medium text-[#004eeb] shadow-sm transition-colors hover:bg-[#d1e0ff] hover:text-blue-600"
                @click="createNewAssociation"
              >
                <PlusIcon class="mr-1.5 h-4 w-4" />
                {{ t("common.createNewAssociation") }}
              </button>
            </div>

            <!-- Association Options -->
            <div
              v-for="option in associationOptions"
              v-else
              :key="option.value"
              class="relative cursor-pointer select-none px-3 py-3 transition-colors hover:bg-gray-50"
              role="option"
              :aria-selected="option.value === associationSettings.selectedAssociation[0]"
              @click="selectOption(option)"
            >
              <div class="block">
                <div class="text-sm font-medium text-gray-900">{{ option.label }}</div>
                <div class="mt-1 text-sm text-gray-500">{{ option.description }}</div>
              </div>
            </div>

            <!-- None Option (when no contact fields) -->
            <div
              v-if="!hasContactFieldsPresent && associationOptions.length > 0"
              class="relative cursor-pointer select-none px-3 py-3 transition-colors hover:bg-gray-50"
              role="option"
              :aria-selected="associationSettings.selectedAssociation.length === 0"
              @click="
                selectOption({
                  value: '',
                  label: t('common.noneAssociation'),
                  description: t('common.noneAssociationDescription'),
                })
              "
            >
              <div class="block">
                <div class="text-sm font-medium text-gray-900">
                  {{ t("common.noneAssociation") }}
                </div>
                <div class="mt-1 text-sm text-gray-500">
                  {{ t("common.noneAssociationDescription") }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="error" class="mt-1 text-xs text-red-500">
          {{ error }}
        </div>
        <div v-if="loading" class="mt-1 text-xs text-gray-500" role="status" aria-live="polite">
          {{ t("common.loadingAssociations") }}
        </div>
      </div>
    </UIModalContent>

    <template #footer>
      <UIModalFooter
        id="contact-association-modal-footer"
        :positive-text="t('common.save')"
        :negative-text="t('common.cancel')"
        :disabled="
          loading ||
          !!error ||
          (associationSettings.selectedAssociation.length === 0 && hasContactFieldsPresent) ||
          (associationOptions.length === 0 && hasContactFieldsPresent)
        "
        @positive-click="submitCallback"
        @negative-click="cancelCallback"
      >
        <template #actionLeft>
          <UIButton id="create-new-association" type="tertiary" @click="createNewAssociation">
            <PlusIcon class="mr-2 h-4 w-4" />
            {{ t("common.createNewAssociation") }}
          </UIButton>
        </template>
      </UIModalFooter>
    </template>
  </UIModal>
</template>
